import { useState, useRef } from 'react';
import useAccessToken, { getAccessTokenOrUndefined } from 'auth/useAccessToken';
import useWebSocket from 'hooks/useWebsocket';
import { companiesAPIUrls } from 'apis/CompanyAPI/companies/companiesApi';
import { logError } from 'util/logging';
import { CompanyOnboardingSuggestions, OnboardingMessageDTO } from 'types/onboarding';

export function useCompanyOnboardingWebsocket(
  companySlug: string,
  onComplete: (suggestions: CompanyOnboardingSuggestions) => void,
  onError: () => void,
  retry: () => void,
) {
  const [ac] = useAccessToken();
  const token = getAccessTokenOrUndefined(ac);
  const [suggestions, setSuggestions] = useState<CompanyOnboardingSuggestions>({});

  const suggestionsRef = useRef<CompanyOnboardingSuggestions>({});

  useWebSocket(
    companiesAPIUrls.onboarding.websocket(companySlug),
    msg => {
      try {
        const parsed = JSON.parse(msg) as OnboardingMessageDTO;
        if (parsed.type === 'suggestion') {
          const suggestion = parsed.suggestion as Partial<CompanyOnboardingSuggestions>;
          setSuggestions(prev => {
            const next = { ...prev, ...suggestion };
            suggestionsRef.current = next;
            return next;
          });
          return;
        }
        if (parsed.type === 'need more time') {
          retry();
          return;
        }
        if (parsed.type === 'completed') {
          const suggestions = parsed.suggestions as Partial<CompanyOnboardingSuggestions>;
          setSuggestions(prev => ({ ...prev, ...suggestions }));
          onComplete(suggestions);
        }
      } catch (e) {
        logError('Received invalid JSON', e);
      }
    },
    onError,
    token,
  );
  return { suggestions, setSuggestions };
}
